import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Building2, Calendar, MapPin, Users } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const Experience: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expandedItem, setExpandedItem] = useState<number | null>(0);

  const experiences = [
    {
      title: "Chief Executive Officer",
      company: "Serengeti Bytes Limited",
      location: "Dar es Salaam, Tanzania",
      period: "2018 - Present",
      type: "Current",
      description: "Leading a full-service communications, public relations, and digital marketing agency specializing in sustainable development and digital transformation initiatives.",
      achievements: [
        "Designed and executed communication strategies for 250+ projects, enhancing client impact in sustainable development",
        "Established partnerships with 110+ clients and media outlets, fostering collaboration between government, private sector, and NGOs",
        "Managed digital marketing campaigns and addressed reputational challenges, ensuring resilient brand narratives",
        "Led multidisciplinary teams to align branding and messaging with organizational goals, amplifying visibility for Tanzanian organizations"
      ],
      teamSize: "15+",
      industry: "Communications & PR"
    },
    {
      title: "Consultant and Member of Ministerial Committee",
      company: "Ministry of Agriculture",
      location: "Dodoma, Tanzania",
      period: "March 2022 - July 2023",
      type: "Previous",
      description: "Contributed to the Build Better Tomorrow (BBT) Youth in Agribusiness (YIA) initiative, rebranding agriculture for 200,000+ youth through innovative behavior change communication.",
      achievements: [
        "Developed communication strategies to position agribusiness as a viable career, driving youth empowerment programs",
        "Mentored youth in incubation centers, contributing to the establishment of 15,000+ youth-led agribusinesses nationwide",
        "Provided strategic insights to the Ministry, addressing challenges in sustainable agriculture communication",
        "Led nationwide audio-visual storytelling for accurate impact reporting and conducted training sessions on business skills"
      ],
      teamSize: "50+",
      industry: "Government & Agriculture"
    },
    {
      title: "Vice Chair, Industrial Advisory Committee",
      company: "Mwalimu Julius K. Nyerere University of Agriculture and Technology",
      location: "Mara, Tanzania",
      period: "2023 - Present",
      type: "Current",
      description: "Advising on industry-aligned education to prepare graduates for Tanzania's evolving job market in agriculture and technology sectors.",
      achievements: [
        "Ensured programs meet labor demands in agriculture and technology",
        "Facilitated partnerships for research, innovation, and student opportunities in digital transformation",
        "Supported public-private partnerships for sustainable university projects",
        "Aligned curriculum with industry needs for better graduate employability"
      ],
      teamSize: "20+",
      industry: "Education & Advisory"
    },
    {
      title: "Founder and Chief Coordinator",
      company: "Tanzania Digital Awards",
      location: "Dar es Salaam, Tanzania",
      period: "2020 - Present",
      type: "Current",
      description: "Pioneered this national platform celebrating digital innovation in Tanzania for societal impact and transformation.",
      achievements: [
        "Established a national platform celebrating digital innovation for societal impact",
        "Designed the awards into a key event recognizing digital transformation leaders",
        "Secured funding from government, private, and civil society stakeholders",
        "Coordinated annual ceremonies honoring leaders in Tanzania's digital ecosystem"
      ],
      teamSize: "10+",
      industry: "Digital Innovation"
    }
  ];

  const toggleExpanded = (index: number) => {
    setExpandedItem(expandedItem === index ? null : index);
  };

  return (
    <section id="experience" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className={`text-4xl md:text-5xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-charcoal'
          }`}>
            Professional <span className="gradient-text font-medium">Experience</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-sunrise-orange to-serengeti-gold mx-auto rounded-full mb-4"></div>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            A journey of leadership, growth, and transformative impact across industries
          </p>
        </div>

        {/* Experience Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className={`absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 transform md:-translate-x-0.5 ${
            isDark ? 'bg-gradient-to-b from-sunrise-orange to-serengeti-gold' : 'bg-gradient-to-b from-sunrise-orange to-serengeti-gold'
          }`}></div>

          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div 
                key={index}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Dot */}
                <div className={`absolute left-8 md:left-1/2 w-4 h-4 rounded-full transform md:-translate-x-1/2 z-10 ${
                  exp.type === 'Current' 
                    ? 'bg-sunrise-orange animate-pulse' 
                    : isDark ? 'bg-gray-600' : 'bg-gray-400'
                }`}></div>

                {/* Content Card */}
                <div className={`ml-16 md:ml-0 md:w-1/2 ${
                  index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'
                }`}>
                  <div className={`glass border rounded-3xl p-6 lg:p-8 shadow-2xl transition-all duration-500 hover:scale-105 cursor-pointer ${
                    isDark ? 'glass-dark' : 'glass-light'
                  } ${expandedItem === index ? 'ring-2 ring-sunrise-orange/50' : ''}`}
                  onClick={() => toggleExpanded(index)}>
                    
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className={`text-xl font-semibold ${
                            isDark ? 'text-white' : 'text-charcoal'
                          }`}>
                            {exp.title}
                          </h3>
                          {exp.type === 'Current' && (
                            <span className="px-2 py-1 text-xs font-medium bg-sunrise-orange/20 text-sunrise-orange rounded-full border border-sunrise-orange/30">
                              Current
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 mb-3">
                          <div className="flex items-center gap-2">
                            <Building2 size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                            <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {exp.company}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {exp.period}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mb-4">
                          <div className="flex items-center gap-2">
                            <MapPin size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {exp.location}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {exp.teamSize} team
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <button className={`p-2 rounded-full transition-transform duration-300 ${
                        isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                      }`}>
                        {expandedItem === index ? (
                          <ChevronUp size={20} className={isDark ? 'text-white' : 'text-gray-700'} />
                        ) : (
                          <ChevronDown size={20} className={isDark ? 'text-white' : 'text-gray-700'} />
                        )}
                      </button>
                    </div>

                    {/* Description */}
                    <p className={`leading-relaxed mb-4 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {exp.description}
                    </p>

                    {/* Expanded Content */}
                    {expandedItem === index && (
                      <div className="mt-6 pt-6 border-t border-gray-300/20 animate-fade-in-up">
                        <h4 className={`text-lg font-medium mb-4 ${
                          isDark ? 'text-white' : 'text-charcoal'
                        }`}>
                          Key Achievements
                        </h4>
                        <ul className="space-y-3">
                          {exp.achievements.map((achievement, achIndex) => (
                            <li key={achIndex} className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                isDark ? 'bg-sunrise-orange' : 'bg-sunrise-orange'
                              }`}></div>
                              <span className={`text-sm leading-relaxed ${
                                isDark ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                {achievement}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
